import { supabase } from '@/lib/supabase'
import type {
  Profile,
  Product,
  Order,
  OrderItem,
  Transaction,
  RechargeRequest,
  Withdrawal,
  KycSubmission,
  SystemSettings,
} from '@/lib/supabase'
import type { CartItem } from '@/mocks/store'
import { ensureProfile, clearAuthCache } from './authService'

// ============================================================
// FILE UPLOAD (via Edge Function — bypasses Storage RLS)
// ============================================================
export async function uploadFile(
  file: File,
  bucket: 'kyc' | 'products' | 'receipts',
  path: string
): Promise<{ url: string; path: string }> {
  const session = await getSession()
  if (!session?.access_token) {
    throw new Error('請先登入再進行上傳')
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('bucket', bucket)
  formData.append('path', path)

  const response = await fetch(
    `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/file-upload`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: formData,
    }
  )

  const result = await response.json().catch(() => ({
    error: response.status >= 500
      ? '伺服器錯誤，請稍後再試 (' + response.status + ')'
      : '上傳失敗 (' + response.status + ')',
  }))

  if (!response.ok) {
    throw new Error(result?.error || ('上傳失敗 (' + response.status + ')'))
  }

  if (!result?.url || typeof result.url !== 'string' || !result.url.startsWith('http')) {
    throw new Error('上傳成功但未取得有效的圖片網址，請聯繫管理員')
  }

  return { url: result.url, path: result.path }
}

// ============================================================
// AUTH (Auto-verify signup via Edge Function)
// ============================================================
export async function signUp(email: string, password: string, meta: { name: string; phone: string; role?: string; level?: string }) {
  clearAuthCache()

  const response = await fetch(
    `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/auto-signup`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        password,
        name: meta.name,
        phone: meta.phone || '',
      }),
    }
  )

  const result = await response.json().catch(() => ({
    error: response.status >= 500
      ? '伺服器錯誤，請稍後再試 (HTTP ' + response.status + ')'
      : '註冊失敗，請檢查網路連線 (' + response.status + ')'
  }))

  if (!response.ok) {
    const errMsg = result?.error || ('註冊失敗 (' + response.status + ')')
    throw new Error(errMsg)
  }

  // After successful Edge Function creation, sign in automatically
  const { data, error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (signInError) {
    // Edge Function 成功建立了帳號，但 signInWithPassword 可能因為
    // profile 觸發器還沒完成而讀不到 profile。這時候用戶實際上是存在的。
    // 我們回傳一個特殊標記，讓前端知道「帳號已建立，請手動登入」
    if (signInError.message.includes('Invalid login credentials')) {
      throw new Error('帳號已建立，但自動登入失敗。請前往登入頁面手動登入。')
    }
    throw new Error('帳號已建立，但自動登入失敗：' + signInError.message + '。請稍後手動登入。')
  }

  if (!data.user) {
    throw new Error('帳號已建立，但無法取得用戶資訊。請手動登入。')
  }

  // 確保 profile 存在（如果觸發器還沒跑完，前端主動補上）
  await ensureProfile(data.user.id, {
    ...data.user.user_metadata,
    email: data.user.email,
  })

  return data
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })
  if (error) throw error
  return data
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

// ============================================================
// PROFILES
// ============================================================
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()
  if (error) throw error
  return data as Profile | null
}

export async function getAllProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase.from('profiles').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as Profile[]) || []
}

export async function updateProfile(userId: string, updates: Partial<Profile>) {
  const { data, error } = await supabase.from('profiles').update(updates).eq('id', userId).select().single()
  if (error) throw error
  return data as Profile
}

export async function adjustBalance(userId: string, delta: number, note?: string) {
  const profile = await getProfile(userId)
  if (!profile) throw new Error('用戶不存在')

  const currentBalance = toMoney(profile.balance)

  if (!Number.isFinite(currentBalance) || currentBalance < 0) {
    throw new Error('目前餘額數值異常，操作已中止')
  }

  // 🛠 修正：toMoney 會把負數歸零，所以這裡改用 raw delta 自行安全轉換
  const absAmount = toMoney(Math.abs(delta))
  const safeDelta = delta > 0 ? absAmount : -absAmount

  const newBalance = currentBalance + safeDelta

  if (newBalance < 0) throw new Error('餘額不可為負')
  if (!Number.isFinite(newBalance) || newBalance > SAFE_MAX_BALANCE) {
    throw new Error('計算後餘額異常，操作已中止')
  }

  // 寫入新餘額
  const updated = await updateProfile(userId, { balance: newBalance })

  // 🛠 強制驗證：確認資料庫確實寫入成功
  const verify = await getProfile(userId)
  const verifyBalance = verify ? toMoney(verify.balance) : -1
  if (verifyBalance !== newBalance) {
    throw new Error('資料庫寫入驗證失敗：扣除後餘額未正確同步')
  }

  await createTransaction({
    id: genTxId(),
    user_id: userId,
    type: safeDelta > 0 ? '儲值' : '扣款',
    amount: safeDelta,
    balance: newBalance,
    description: safeDelta > 0
      ? (note || '管理員人工入帳')
      : (note || '管理員人工扣除'),
  })
  return newBalance
}

// ============================================================
// PRODUCTS
// ============================================================
export async function getAllProducts(): Promise<Product[]> {
  const { data, error } = await supabase.from('products').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as Product[]) || []
}

export async function getProductById(id: string): Promise<Product | null> {
  const { data, error } = await supabase.from('products').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as Product | null
}

export async function createProduct(product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase.from('products').insert([product as Record<string, unknown>]).select()
  if (error) {
    console.error('[createProduct] Supabase error:', error)
    throw new Error(`新增商品失敗: ${error.message} (代碼: ${error.code})`)
  }
  return (data as Product[])[0] as Product
}

export async function updateProduct(id: string, updates: Partial<Product>) {
  const { data, error } = await supabase.from('products').update(updates).eq('id', id).select().single()
  if (error) {
    console.error('[updateProduct] Supabase error:', error)
    throw new Error(`更新商品失敗: ${error.message} (代碼: ${error.code})`)
  }
  return data as Product
}

export async function deleteProduct(id: string) {
  const { error } = await supabase.from('products').delete().eq('id', id)
  if (error) throw error
}

// ============================================================
// ORDERS
// ============================================================
export async function getAllOrders(): Promise<Order[]> {
  // 優先嘗試 Edge Function 獲取（繞過 RLS 限制，確保 admin 一定能讀到所有訂單）
  try {
    const session = await getSession()
    if (session?.access_token) {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-orders-list`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      )
      if (response.ok) {
        const result = await response.json()
        if (Array.isArray(result.data)) {
          return result.data as Order[]
        }
      }
    }
  } catch (e) {
    console.warn('[getAllOrders] Edge Function fallback:', e)
  }

  // 回退到直接查詢（受 RLS 限制）
  const { data, error } = await supabase.from('orders').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as Order[]) || []
}

export async function getOrdersByUser(userId: string): Promise<Order[]> {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as Order[]) || []
}

export async function getOrderById(orderId: string): Promise<Order | null> {
  const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).maybeSingle()
  if (error) throw error
  return data as Order | null
}

export async function createOrder(order: Order, items: Omit<OrderItem, 'id'>[]) {
  const { data: orderData, error: orderError } = await supabase
    .from('orders')
    .insert([order as Record<string, unknown>])
    .select()
    .single()
  if (orderError) throw orderError

  const orderItems = items.map((item) => ({ ...item, order_id: order.id }))
  const { error: itemsError } = await supabase.from('order_items').insert(orderItems as Record<string, unknown>[])
  if (itemsError) throw itemsError

  return orderData as Order
}

export async function shipOrder(orderId: string, trackingNumber: string) {
  const { data, error } = await supabase
    .from('orders')
    .update({ status: '已發貨', tracking_number: trackingNumber, shipped_at: new Date().toISOString() })
    .eq('id', orderId)
    .select()
    .single()
  if (error) throw error
  return data as Order
}

export async function completeOrder(orderId: string) {
  // Get order items with product_id for stock restoration
  const { data: items, error: itemsError } = await supabase.from('order_items').select('*').eq('order_id', orderId)
  if (itemsError) throw itemsError

  const { data: order, error: orderError } = await supabase.from('orders').select('*').eq('id', orderId).maybeSingle()
  if (orderError) throw orderError
  if (!order) throw new Error('訂單不存在')

  const refund = (items as OrderItem[]).reduce(
    (sum, item) => sum + toMoney(item.suggested_price) * toMoney(item.quantity), 0
  )

  const profile = await getProfile(order.user_id)
  if (!profile) throw new Error('用戶不存在')

  const currentBalance = toMoney(profile.balance)
  const newBalance = currentBalance + refund
  await updateProfile(order.user_id, { balance: newBalance })

  await supabase.from('orders').update({ status: '已完成' }).eq('id', orderId)

  // Restore stock for each product in the order
  for (const item of items as OrderItem[]) {
    if (item.product_id) {
      const { data: product } = await supabase
        .from('products')
        .select('stock')
        .eq('id', item.product_id)
        .maybeSingle()
      if (product) {
        const newStock = product.stock + item.quantity
        await supabase.from('products').update({ stock: newStock }).eq('id', item.product_id)
      }
    }
  }

  await createTransaction({
    id: genTxId(),
    user_id: order.user_id,
    type: '儲值',
    amount: refund,
    balance: newBalance,
    description: `訂單 ${orderId} 銷售結案：全額返款 (含本金與利潤)`,
  })

  return refund
}

export async function getOrderItems(orderId: string): Promise<OrderItem[]> {
  const { data, error } = await supabase.from('order_items').select('*').eq('order_id', orderId)
  if (error) throw error
  return (data as OrderItem[]) || []
}

// ============================================================
// EXPERIENCE SETTLEMENT (independent from core distribution system)
// ============================================================
export async function settleExperience(userId: string): Promise<{ success: true; experienceAmount: number; profit: number } | { success: false; error: string }> {
  try {
    // 1. Get all COMPLETED orders by user (體驗結算只看「已完成」訂單)
    const orders = await getOrdersByUser(userId);
    const completedOrders = orders.filter((o: Order) => o.status === "已完成");

    let experienceCredit = 0;
    let totalProfit = 0;
    let totalGroups = 0;
    const experienceOrderIds: string[] = [];

    // 2. Identify experience orders by checking products in order_items
    for (const order of completedOrders) {
      const items = await getOrderItems(order.id);
      if (items.length === 0) continue;

      let isExperienceOrder = true;
      let orderCredit = 0;
      let orderProfit = 0;
      let orderGroups = 0;

      for (const item of items) {
        if (!item.product_id) {
          isExperienceOrder = false;
          break;
        }
        const product = await getProductById(item.product_id);
        if (!product || product.region !== "體驗認購區") {
          isExperienceOrder = false;
          break;
        }
        const qty = toMoney(item.quantity);
        const itemCredit = toMoney(item.distribution_price) * qty;
        const itemProfit = (toMoney(item.suggested_price) - toMoney(item.distribution_price)) * qty;
        orderCredit += itemCredit;
        orderProfit += itemProfit;
        orderGroups += qty;
      }

      if (isExperienceOrder) {
        experienceOrderIds.push(order.id);
        experienceCredit += orderCredit;
        totalProfit += orderProfit;
        totalGroups += orderGroups;
      }
    }

    // 判定條件：必須滿 10 組已完成體驗訂單才能結算
    if (totalGroups < 10) {
      return { success: false, error: "請完成所有體驗訂單後再結算" };
    }

    const totalDeduct = experienceCredit + totalProfit;
    if (totalDeduct <= 0) {
      return { success: false, error: "無可結算的體驗認購訂單" };
    }

    // 3. 直接強制將餘額歸零，檢查 update 結果確保成功才回傳
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ balance: 0, experience_active: false })
      .eq('id', userId);

    if (updateError) {
      return { success: false, error: "餘額更新失敗：" + updateError.message };
    }

    // 4. 嘗試寫入交易紀錄，失敗不影響結算成功
    try {
      await createTransaction({
        id: genTxId(),
        user_id: userId,
        type: "體驗金回收",
        amount: -totalDeduct,
        balance: 0,
        description: "體驗專案結束：系統回收本金與收益",
      });
    } catch (txErr) {
      console.warn("[settleExperience] 交易紀錄寫入失敗（不影響結算成功）:", txErr);
    }

    return { success: true, experienceAmount: totalDeduct, profit: totalProfit };
  } catch (err: unknown) {
    let msg = "體驗金回收失敗";
    if (err instanceof Error && err.message) msg = err.message;
    return { success: false, error: msg };
  }
}

// ============================================================
// TRANSACTIONS
// ============================================================
export async function getTransactionsByUser(userId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as Transaction[]) || []
}

export async function getAllTransactions(): Promise<Transaction[]> {
  const { data, error } = await supabase.from('transactions').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as Transaction[]) || []
}

export async function createTransaction(tx: Omit<Transaction, 'id' | 'created_at'> & { id: string }) {
  const { error } = await supabase.from('transactions').insert([tx as Record<string, unknown>])
  if (error) throw error
}

// ============================================================
// RECHARGE REQUESTS
// ============================================================
export async function getAllRechargeRequests(): Promise<RechargeRequest[]> {
  const { data, error } = await supabase.from('recharge_requests').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as RechargeRequest[]) || []
}

export async function getRechargeRequestsByUser(userId: string): Promise<RechargeRequest[]> {
  const { data, error } = await supabase
    .from('recharge_requests')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as RechargeRequest[]) || []
}

export async function createRechargeRequest(req: Omit<RechargeRequest, 'id' | 'status' | 'created_at' | 'reviewed_at'>) {
  const requestId = 'RC' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5).toUpperCase()

  const safeReq = {
    ...req,
    id: requestId,
    amount: toMoney(req.amount),
    // Ensure transfer_code is never empty — some DB constraints reject ''
    transfer_code: req.transfer_code && req.transfer_code.trim() ? req.transfer_code.trim() : '-',
  }
  const { data, error } = await supabase
    .from('recharge_requests')
    .insert([safeReq as Record<string, unknown>])
    .select()
    .single()
  if (error) {
    console.error('[createRechargeRequest] Supabase error:', error)
    throw new Error(`儲值申請失敗：${error.message}`)
  }
  return data as RechargeRequest
}

export async function approveRechargeRequest(requestId: string) {
  const { data: req, error: fetchError } = await supabase
    .from('recharge_requests')
    .select('*')
    .eq('id', requestId)
    .maybeSingle()
  if (fetchError) throw fetchError
  if (!req) throw new Error('申請不存在')
  if (req.status !== '待審核') throw new Error('申請已審核')

  const reviewedAt = new Date().toISOString()

  await supabase.from('recharge_requests').update({ status: '通過', reviewed_at: reviewedAt }).eq('id', requestId)

  const profile = await getProfile(req.user_id)
  if (profile) {
    const addAmount = toMoney(req.amount)
    const currentBalance = toMoney(profile.balance)
    const currentPending = toMoney(profile.pending_balance)
    const newBalance = currentBalance + addAmount
    const newPending = Math.max(0, currentPending - addAmount)
    await updateProfile(req.user_id, { balance: newBalance, pending_balance: newPending })

    await createTransaction({
      id: genTxId(),
      user_id: req.user_id,
      type: '儲值',
      amount: addAmount,
      balance: newBalance,
      description: '儲值審核通過入帳',
    })
  }
}

export async function rejectRechargeRequest(requestId: string, adminNote: string) {
  const { data: req, error: fetchError } = await supabase
    .from('recharge_requests')
    .select('*')
    .eq('id', requestId)
    .maybeSingle()
  if (fetchError) throw fetchError
  if (!req) throw new Error('申請不存在')
  if (req.status !== '待審核') throw new Error('申請已審核')

  const reviewedAt = new Date().toISOString()

  await supabase
    .from('recharge_requests')
    .update({ status: '拒絕', admin_note: adminNote, reviewed_at: reviewedAt })
    .eq('id', requestId)

  const profile = await getProfile(req.user_id)
  if (profile) {
    const currentPending = toMoney(profile.pending_balance)
    const reqAmount = toMoney(req.amount)
    const newPending = Math.max(0, currentPending - reqAmount)
    await updateProfile(req.user_id, { pending_balance: newPending })
  }
}

// ============================================================
// WITHDRAWALS
// ============================================================
export async function getAllWithdrawals(): Promise<Withdrawal[]> {
  const { data, error } = await supabase.from('withdrawals').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data as Withdrawal[]) || []
}

export async function getWithdrawalsByUser(userId: string): Promise<Withdrawal[]> {
  const { data, error } = await supabase
    .from('withdrawals')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data as Withdrawal[]) || []
}

export async function createWithdrawal(wd: Omit<Withdrawal, 'id' | 'status' | 'created_at' | 'reviewed_at'>) {
  const profile = await getProfile(wd.user_id)
  if (!profile) throw new Error('用戶不存在')

  const safeAmount = toMoney(wd.amount)
  if (safeAmount <= 0) throw new Error('提現金額需大於 0')

  const currentBalance = toMoney(profile.balance)
  if (currentBalance < safeAmount) throw new Error('餘額不足')

  const newBalance = currentBalance - safeAmount
  await updateProfile(wd.user_id, { balance: newBalance })

  const now = new Date().toISOString()
  const withdrawalId = 'WD' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5).toUpperCase()

  const { data, error } = await supabase
    .from('withdrawals')
    .insert([{ ...wd, amount: safeAmount, id: withdrawalId, status: '待審核', created_at: now } as Record<string, unknown>])
    .select()
    .single()
  if (error) throw error

  await createTransaction({
    id: genTxId(),
    user_id: wd.user_id,
    type: '扣款',
    amount: -safeAmount,
    balance: newBalance,
    description: `提現申請 ${withdrawalId}：扣除點數`,
  })

  return data as Withdrawal
}

export async function approveWithdrawal(withdrawalId: string) {
  const { data: req, error: fetchError } = await supabase
    .from('withdrawals')
    .select('*')
    .eq('id', withdrawalId)
    .maybeSingle()
  if (fetchError) throw fetchError
  if (!req) throw new Error('提現申請不存在')
  if (req.status !== '待審核') throw new Error('申請已審核')

  const reviewedAt = new Date().toISOString()
  await supabase.from('withdrawals').update({ status: '已完成', reviewed_at: reviewedAt }).eq('id', withdrawalId)

  const profile = await getProfile(req.user_id)
  if (profile) {
    await createTransaction({
      id: genTxId(),
      user_id: req.user_id,
      type: '儲值',
      amount: 0,
      balance: profile.balance,
      description: `提現申請 ${withdrawalId}：已核准並完成匯款`,
    })
  }
}

export async function rejectWithdrawal(withdrawalId: string, adminNote: string) {
  const { data: req, error: fetchError } = await supabase
    .from('withdrawals')
    .select('*')
    .eq('id', withdrawalId)
    .maybeSingle()
  if (fetchError) throw fetchError
  if (!req) throw new Error('提現申請不存在')
  if (req.status !== '待審核') throw new Error('申請已審核')

  const reviewedAt = new Date().toISOString()
  await supabase
    .from('withdrawals')
    .update({ status: '已退回', admin_note: adminNote, reviewed_at: reviewedAt })
    .eq('id', withdrawalId)

  const profile = await getProfile(req.user_id)
  if (profile) {
    const refundAmount = toMoney(req.amount)
    const currentBalance = toMoney(profile.balance)
    const newBalance = currentBalance + refundAmount
    await updateProfile(req.user_id, { balance: newBalance })

    await createTransaction({
      id: genTxId(),
      user_id: req.user_id,
      type: '儲值',
      amount: refundAmount,
      balance: newBalance,
      description: `提現申請 ${withdrawalId}：已退回，金額返還錢包 (${adminNote})`,
    })
  }
}

// ============================================================
// KYC
// ============================================================
export async function getAllKycSubmissions(): Promise<KycSubmission[]> {
  // 優先嘗試 Edge Function 獲取（繞過 RLS 限制，確保 admin 一定能讀到所有資料）
  try {
    const session = await getSession()
    if (session?.access_token) {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-kyc-list`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        }
      )
      if (response.ok) {
        const result = await response.json()
        if (Array.isArray(result.data)) {
          return result.data as KycSubmission[]
        }
      }
    }
  } catch (e) {
    console.warn('[getAllKycSubmissions] Edge Function fallback:', e)
  }

  // 回退到直接查詢（受 RLS 限制）
  const { data, error } = await supabase.from('kyc_submissions').select('*').order('submitted_at', { ascending: false })
  if (error) throw error
  return (data as KycSubmission[]) || []
}

export async function getKycByUser(userId: string): Promise<KycSubmission | null> {
  const { data, error } = await supabase
    .from('kyc_submissions')
    .select('*')
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return data as KycSubmission | null
}

export async function submitKyc(data: Omit<KycSubmission, 'id' | 'status' | 'submitted_at' | 'reviewed_at'>) {
  const now = new Date().toISOString()
  const id = 'KYC' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5).toUpperCase()

  const { data: result, error } = await supabase
    .from('kyc_submissions')
    .insert([{ ...data, id, status: '待審核', submitted_at: now } as Record<string, unknown>])
    .select()
    .single()
  if (error) throw error

  await supabase
    .from('profiles')
    .update({
      kyc_status: '審核中',
      kyc_real_name: data.real_name,
      kyc_id_number: data.id_number,
      kyc_front_photo: data.front_photo,
      kyc_back_photo: data.back_photo,
      kyc_holding_photo: data.holding_photo,
      kyc_submitted_at: now,
    })
    .eq('id', data.user_id)

  return result as KycSubmission
}

export async function approveKyc(submissionId: string) {
  const { data: sub, error } = await supabase
    .from('kyc_submissions')
    .select('*')
    .eq('id', submissionId)
    .maybeSingle()
  if (error) throw error
  if (!sub) throw new Error('申請不存在')

  const reviewedAt = new Date().toISOString()
  await supabase.from('kyc_submissions').update({ status: '已通過', reviewed_at: reviewedAt }).eq('id', submissionId)

  await supabase
    .from('profiles')
    .update({
      kyc_status: '已通過',
      kyc_reviewed_at: reviewedAt,
    })
    .eq('id', sub.user_id)
}

export async function rejectKyc(submissionId: string, adminNote: string) {
  const { data: sub, error } = await supabase
    .from('kyc_submissions')
    .select('*')
    .eq('id', submissionId)
    .maybeSingle()
  if (error) throw error
  if (!sub) throw new Error('申請不存在')

  const reviewedAt = new Date().toISOString()
  await supabase
    .from('kyc_submissions')
    .update({ status: '已拒絕', admin_note: adminNote, reviewed_at: reviewedAt })
    .eq('id', submissionId)

  await supabase
    .from('profiles')
    .update({
      kyc_status: '已拒絕',
      kyc_admin_note: adminNote,
      kyc_reviewed_at: reviewedAt,
    })
    .eq('id', sub.user_id)
}

// ============================================================
// SYSTEM SETTINGS
// ============================================================
export async function getSystemSettings(): Promise<SystemSettings> {
  const { data, error } = await supabase.from('system_settings').select('*').eq('id', 1).maybeSingle()
  if (error) throw error
  return (data as SystemSettings) || {
    id: 1,
    site_line_url: 'https://line.me/ti/p/-xtd_8xuUf',
    bank_name: '台灣銀行',
    account_name: '吉時國際貿易股份有限公司',
    account_number: '012-1234-5678-9012',
    bank_branch: '台北信義分行',
    recharge_note: '轉帳完成後請記得填寫後五碼並上傳憑證',
    updated_at: new Date().toISOString(),
  }
}

export async function updateSystemSettings(updates: Partial<SystemSettings>) {
  const { data, error } = await supabase.from('system_settings').update(updates).eq('id', 1).select().single()
  if (error) throw error
  return data as SystemSettings
}

// ============================================================
// ORDER FROM CART (checkout helper)
// ============================================================
export async function createOrderFromCart(
  userId: string,
  userName: string,
  cartItems: { productId: string; productName: string; productImage: string; distributionPrice: number; suggestedPrice: number; quantity: number }[]
): Promise<{ success: boolean; orderId?: string; error?: string }> {
  try {
    // 1. Verify user & balance
    const profile = await getProfile(userId)
    if (!profile) {
      console.error("[createOrderFromCart] 用戶不存在:", userId)
      return { success: false, error: "用戶不存在，請重新登入" }
    }

    const totalAmount = cartItems.reduce((sum, item) => sum + toMoney(item.distributionPrice) * toMoney(item.quantity), 0)
    if (toMoney(profile.balance) < totalAmount) {
      console.error("[createOrderFromCart] 餘額不足:", profile.balance, "<", totalAmount)
      return { success: false, error: `餘額不足（目前 $${profile.balance}，需要 $${totalAmount}），請先聯繫團長儲值` }
    }

    // 2. Validate stock (query latest inventory from DB)
    const stockMap = new Map<string, number>() // productId -> currentStock
    for (const item of cartItems) {
      if (!item.productId) {
        return { success: false, error: `商品「${item.productName}」無法關聯到庫存，請重新加入購物車` }
      }
      if (stockMap.has(item.productId)) continue // already checked

      const { data: p, error: stockErr } = await supabase
        .from("products")
        .select("stock")
        .eq("id", item.productId)
        .maybeSingle()
      if (stockErr || !p) {
        return { success: false, error: `商品「${item.productName}」不存在或已下架` }
      }
      if (p.stock < item.quantity) {
        return { success: false, error: `商品「${item.productName}」庫存不足（剩餘 ${p.stock} 件，需要 ${item.quantity} 件）` }
      }
      stockMap.set(item.productId, p.stock)
    }

    // 3. Deduct balance
    const currentBalance = toMoney(profile.balance)
    const newBalance = currentBalance - totalAmount
    const { error: updateError } = await supabase
      .from("profiles")
      .update({ balance: newBalance })
      .eq("id", userId)
    if (updateError) {
      console.error("[createOrderFromCart] 扣款失敗 (profiles update):", updateError)
      const errCode = (updateError as { code?: string }).code || "UNKNOWN"
      const errMsg = updateError.message || "無詳細訊息"
      const errDetails = (updateError as { details?: string }).details || ""
      return { success: false, error: `[${errCode}] 扣款失敗：${errMsg}${errDetails ? " (" + errDetails + ")" : ""}` }
    }

    // 4. Generate order ID
    const now = new Date()
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "")
    const rand = Math.floor(Math.random() * 9000 + 1000)
    const orderId = `ORD${dateStr}-${rand}`

    // 5. Create order
    const order: Order = {
      id: orderId,
      user_id: userId,
      user_name: userName,
      total_amount: totalAmount,
      status: "待發貨",
      tracking_number: null,
      shipping_address: null,
      remark: null,
      created_at: now.toISOString(),
      shipped_at: null,
    }

    const { error: orderInsertError } = await supabase
      .from("orders")
      .insert([order as Record<string, unknown>])
    if (orderInsertError) {
      console.error("[createOrderFromCart] 建立訂單失敗:", orderInsertError)
      // Rollback balance
      await supabase.from("profiles").update({ balance: profile.balance }).eq("id", userId)
      const errCode = (orderInsertError as { code?: string }).code || "UNKNOWN"
      const errMsg = orderInsertError.message || "無詳細訊息"
      const errDetails = (orderInsertError as { details?: string }).details || ""
      return { success: false, error: `[${errCode}] 建立訂單失敗：${errMsg}${errDetails ? " (" + errDetails + ")" : ""}` }
    }

    // 6. Create order items with real product_id
    const orderItems = cartItems.map((item) => ({
      order_id: orderId,
      product_id: item.productId,
      product_name: item.productName,
      product_image: item.productImage || null,
      distribution_price: item.distributionPrice,
      suggested_price: item.suggestedPrice,
      quantity: item.quantity,
      created_at: now.toISOString(),
    }))

    const { error: itemsError } = await supabase
      .from("order_items")
      .insert(orderItems as Record<string, unknown>[])
    if (itemsError) {
      console.error("[createOrderFromCart] 建立訂單項目失敗:", itemsError)
      // Rollback: delete order + restore balance
      await supabase.from("orders").delete().eq("id", orderId)
      await supabase.from("profiles").update({ balance: profile.balance }).eq("id", userId)
      const errCode = (itemsError as { code?: string }).code || "UNKNOWN"
      const errMsg = itemsError.message || "無詳細訊息"
      const errDetails = (itemsError as { details?: string }).details || ""
      return { success: false, error: `[${errCode}] 建立訂單明細失敗：${errMsg}${errDetails ? " (" + errDetails + ")" : ""}` }
    }

    // 7. Deduct stock (critical step)
    const deductedProducts: { productId: string; originalStock: number }[] = []
    for (const item of cartItems) {
      if (!item.productId) continue
      const originalStock = stockMap.get(item.productId)!
      const newStock = originalStock - item.quantity
      const { error: stockError } = await supabase
        .from("products")
        .update({ stock: newStock })
        .eq("id", item.productId)
      if (stockError) {
        console.error("[createOrderFromCart] 扣減庫存失敗:", stockError)
        // Rollback everything: restore stock for already-deducted items, delete order, restore balance
        for (const d of deductedProducts) {
          await supabase.from("products").update({ stock: d.originalStock }).eq("id", d.productId)
        }
        await supabase.from("orders").delete().eq("id", orderId)
        await supabase.from("profiles").update({ balance: profile.balance }).eq("id", userId)
        return { success: false, error: `庫存扣減失敗：${item.productName}，請稍後再試` }
      }
      deductedProducts.push({ productId: item.productId, originalStock })
    }

    // 8. Create transaction record
    const { error: txError } = await supabase.from("transactions").insert([{
      id: genTxId(),
      user_id: userId,
      type: "扣款",
      amount: -toMoney(totalAmount),
      balance: newBalance,
      description: `訂單 ${orderId}`,
      created_at: now.toISOString(),
    } as Record<string, unknown>])
    if (txError) {
      console.error("[createOrderFromCart] 建立交易紀錄失敗:", txError)
      // Non-fatal: order already created and stock already deducted. Log but don't block.
    }

    console.log("[createOrderFromCart] 下單成功:", orderId, "扣款:", totalAmount, "新餘額:", newBalance)

    // 9. 更新會員累計訂單數（+1 筆訂單）
    try {
      const currentTotalOrders = Number(profile.total_orders) || 0
      const newTotalOrders = currentTotalOrders + 1
      await supabase.from('profiles').update({ total_orders: newTotalOrders }).eq('id', userId)
      console.log("[createOrderFromCart] 累計訂單已更新:", newTotalOrders)
    } catch (e) {
      console.error("[createOrderFromCart] 更新累計訂單失敗:", e)
      // Non-fatal: 訂單已建立，累計訂單更新失敗不影響主流程
    }

    return { success: true, orderId }
  } catch (err: unknown) {
    let msg = "未知錯誤"
    if (err instanceof Error) {
      msg = err.message
    } else if (typeof err === "string") {
      msg = err
    } else if (err && typeof err === "object") {
      try { msg = JSON.stringify(err) } catch { /* ignore */ }
    }
    console.error("[createOrderFromCart] 未捕獲異常:", err)
    return { success: false, error: "系統異常：" + msg }
  }
}

// ============================================================
// CART (keep in localStorage for simplicity)
// ============================================================
const CART_KEY = 'jishi_supabase_cart'

export function loadCart(): CartItem[] {
  const raw = localStorage.getItem(CART_KEY)
  if (!raw) return []
  try {
    return JSON.parse(raw) as CartItem[]
  } catch {
    return []
  }
}

export function saveCart(cart: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(cart))
}

export function clearCart() {
  localStorage.removeItem(CART_KEY)
}

// ============================================================
// HELPERS
// ============================================================

const SAFE_MAX_BALANCE = 999999999999; // ~1 trillion cap

/**
 * Convert a number or string to a safe integer monetary value.
 * Uses strict integer parsing (parseInt) to eliminate any floating-point
 * drift. All money values in this system must be whole integers.
 * Values exceeding SAFE_MAX_BALANCE are clamped to 0 to prevent overflow.
 */
export function toMoney(value: number | string | null | undefined): number {
  if (value == null) return 0
  const str = String(value).replace(/,/g, '').trim()
  const num = parseInt(str, 10)
  if (Number.isNaN(num)) return 0
  if (!Number.isFinite(num)) return 0
  if (num < 0) return 0
  if (num > SAFE_MAX_BALANCE) return 0
  return num
}

/**
 * Validate that a user-facing amount input is a positive integer.
 * Returns { ok: true, value } or { ok: false, error }.
 */
export function validateMoneyInput(raw: string | number): { ok: true; value: number } | { ok: false; error: string } {
  const str = typeof raw === 'number' ? String(raw) : raw.trim()
  if (!str) return { ok: false, error: '請輸入金額' }
  const clean = str.replace(/,/g, '')
  if (!/^\d+$/.test(clean)) return { ok: false, error: '金額必須為整數，不可含小數點' }
  const num = parseInt(clean, 10)
  if (Number.isNaN(num)) return { ok: false, error: '金額必須為數字' }
  if (num <= 0) return { ok: false, error: '金額必須大於 0' }
  if (num > SAFE_MAX_BALANCE) return { ok: false, error: '金額過大' }
  return { ok: true, value: num }
}

function genTxId(): string {
  return 'tx' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
}

export function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('zh-TW', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(/\//g, '-')
}